import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { StateStoreService } from '../services/state-store/state-store';

/**
 * Billing route gate — `billing_read` on the caller's **home** org.
 *
 * Replaces `adminGuard`, which asked a question the sidebar entry had
 * already stopped asking. `showBillingMenuItem` moved to
 * `holdsHomeCapability('billing_read', …)` so that a delegate granted the
 * Billing area would see the entry; the route was left on the role check.
 * The two then disagreed for exactly the people the capability exists for —
 * a member holding `billing_read` with no ADMIN row saw the menu item and
 * was bounced to `/assessment` on click, with nothing on screen to say why.
 *
 * `isAdmin()` survives as the **legacy answer**, not as a second condition.
 * `holdsHomeCapability` returns it verbatim for any org the backend composed
 * no access block for, so an unconfigured org behaves exactly as it did
 * under `adminGuard`. Absent access data is permissive, never "no
 * capabilities" — see `OrgAccess` in `models/access-control.model.ts`. That
 * ordering is what makes this a widening for configured orgs and a no-op for
 * everyone else, rather than a lockout on the day the epic ships.
 *
 * Home rather than effective, matching what the page acts on: Billing reads
 * `selectedOrgId()`, so picking a customer does not change whose
 * subscription it manages.
 *
 * Deliberately **not** mirroring the menu's `isSuperManager() !== true` half.
 * That hides a dead-end entry for orgs entitled by `org_type`; it is not an
 * authorisation statement, and enforcing it here would newly bounce a
 * SUPER_MANAGER admin who deep-links.
 *
 * `billing_read` is the page gate. The write actions on it (change plan,
 * cancel, manage payment method) are a separate question — `billing_write`
 * — and are not gated at the time of writing.
 *
 * Readiness is guaranteed by `appReadyMatch` at the route level:
 * `_currentMemberRole` is populated before ready flips, which is what stops
 * a refresh on this route bouncing while the role fetch is still in flight.
 */
export const billingGuard: CanActivateFn = () => {
  const state = inject(StateStoreService);
  const router = inject(Router);

  return state.holdsHomeCapability('billing_read', state.isAdmin())
    ? true
    : router.createUrlTree(['/assessment']);
};
